import React, { useEffect, useMemo } from "react";
import type { CalendarApi } from "@fullcalendar/react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import dayjs from "dayjs";

import SVG from "@/assets/svg";
import { Medication, Visit } from "@/interfaces/IServiceUser";
import { TaskManagementResponse } from "@/interfaces/ITaskManagement";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setCurrentTab } from "@/redux/reducers/roster";
import { getRosterMedications } from "@/services/service-users/medication";
import { getStaffForRoster } from "@/services/staff";
import { getVisitTasks } from "@/services/task-management";
import { getId } from "@/utils/helpers";
import { getOccurrenceVisitColor } from "@/utils/visitOccurrence";
import { displayText } from "@/utils/displayValue";
import logger from "@/runtime-utils/observability/logger";
import { accessibleInteractiveColors } from "@/theme/accessibility";
import CustomAvatar from "../avatar";
import CareTeam from "./CareTeam";
import DailyNotes from "./DailyNotes";
import DetailTab from "./DetailTab";
import {
  DrawerStyled,
  schedulerDrawerContentSx,
  schedulerDrawerSx,
  schedulerDrawerTabsSx,
} from "./drawerStyled";
import Medications from "./Medications";
import Tasks from "./Tasks";

type Tab = {
  [key: string]: React.ComponentType<{
    tasks: TaskManagementResponse[];
    setTasks: React.Dispatch<React.SetStateAction<TaskManagementResponse[]>>;
    loading: boolean;
    staffLogLoading: boolean;
    updateVisit: (
      id: string,
      visit: any,
      oldVisit: Partial<Visit>
    ) => Promise<void>;
    staffLogCount: Record<string, number>;
    medications: Medication[];
    medicationLoading: boolean;
  }>;
};

export const TABS = [
  {
    label: "Visit info",
    value: "details",
  },
  {
    label: "Care team",
    value: "careteam",
  },
  {
    label: "Tasks",
    value: "activities",
  },
  {
    label: "Daily Notes",
    value: "observations",
  },
  // {
  //   label: "Medications",
  //   value: "medications",
  // },
];

interface SchedulerDrawerProps {
  open: boolean;
  onClose: () => void;
  getCalendarApi: () => CalendarApi | null;
  updateVisit: (
    id: string,
    visit: any,
    oldVisit: Partial<Visit>
  ) => Promise<void>;
}

function SchedulerDrawer({
  open,
  onClose,
  updateVisit,
  getCalendarApi,
}: SchedulerDrawerProps) {
  const { selectedVisit, currentTab } = useAppSelector((state) => state.roster);

  const dispatch = useAppDispatch();

  const [loading, setLoading] = React.useState(false);
  const [tasks, setTasks] = React.useState<TaskManagementResponse[]>([]);
  const [medications, setMedications] = React.useState<Medication[]>([]);
  const [medicationLoading, setMedicationLoading] = React.useState(false);
  const [staffLogCount, setStaffLogCount] = React.useState<
    Record<string, number>
  >({});
  const [staffLogLoading, setStaffLogLoading] = React.useState(false);

  const extendedProps = selectedVisit?.extendedProps as Visit;
  const isUnAllocated =
    selectedVisit?.getResources().some((resource) => resource.id === "0") ||
    selectedVisit?.title === "Unallocated";

  // The drawer opens on one occurrence, so its badge is that occurrence's
  // status, worked out the same way as the timeline colour and the tooltip.
  // The API's visit.status describes the whole series of a recurring visit.
  const resourceId = selectedVisit?.getResources()?.[0]?.id;
  const occurrenceColor = getOccurrenceVisitColor(
    extendedProps,
    selectedVisit?.start,
    extendedProps?.carerAssigned?.some((carer) => getId(carer) === resourceId)
      ? resourceId
      : undefined
  );

  const staffList = useMemo(
    () =>
      [
        ...(extendedProps?.carerAssigned || []),
        ...(extendedProps?.anotherCarers || []).map((el) => el.carer),
      ].map((el) => el._id),
    [selectedVisit]
  );

  const tabLookup: Tab = {
    details: DetailTab,
    activities: Tasks,
    observations: DailyNotes,
    medications: Medications,
  };

  const renderTab = (tab: string) => {
    if (tab === "careteam") {
      return (
        <CareTeam
          staffLogCount={staffLogCount}
          staffLogLoading={staffLogLoading}
          updateVisit={updateVisit}
          getCalendarApi={getCalendarApi}
        />
      );
    }

    const TabComponent = tabLookup[tab];
    return TabComponent ? (
      <TabComponent
        tasks={tasks}
        setTasks={setTasks}
        staffLogCount={staffLogCount}
        loading={loading}
        staffLogLoading={staffLogLoading}
        medications={medications}
        medicationLoading={medicationLoading}
        updateVisit={updateVisit}
      />
    ) : null;
  };

  const fetchTasksAndNotes = async () => {
    try {
      setLoading(true);
      if (!selectedVisit) return;

      const [tasksResponse] = await Promise.all([
        getVisitTasks({
          serviceUser: selectedVisit?.extendedProps?.serviceUser?.id,
          date: selectedVisit?.start,
          session: selectedVisit?.extendedProps?.session,
          visitId: selectedVisit?.extendedProps?._id,
          visitOriginId: selectedVisit?.extendedProps?.origin?._id,
        }),
      ]);

      const { success, data } = tasksResponse;

      if (success) {
        setTasks(data);
      }
    } catch (error) {
      logger.error("error", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRosterMedications = async () => {
    try {
      setMedicationLoading(true);
      if (!selectedVisit) return;

      const response = await getRosterMedications({
        serviceUser: selectedVisit?.extendedProps?.serviceUser?.id,
        date: selectedVisit?.start,
        session: selectedVisit?.extendedProps?.session,
      });

      if (response.success) {
        setMedications(response.data);
      }
    } catch (error) {
      logger.error(error);
    } finally {
      setMedicationLoading(false);
    }
  };

  const fetchStaffLogs = async () => {
    try {
      if (!staffList.length) return;
      setStaffLogLoading(true);
      const response = await getStaffForRoster({
        staffIds: staffList,
      });

      if (response.success) {
        setStaffLogCount(response.data);
      }
    } catch (error) {
      logger.error(error);
    } finally {
      setStaffLogLoading(false);
    }
  };

  useEffect(() => {
    fetchTasksAndNotes();
    // fetchRosterMedications();
  }, [selectedVisit?.id]);

  useEffect(() => {
    fetchStaffLogs();
  }, [staffList]);

  return (
    <DrawerStyled>
      <Drawer
        sx={schedulerDrawerSx}
        anchor="right"
        open={open}
        onClose={onClose}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column",
            position: "sticky",
            top: 0,
            background: "#FFF",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              minWidth: 0,
              padding: "15px 10px",
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flex: "1 1 auto",
                minWidth: 0,
              }}
            >
              <CustomAvatar
                name={displayText(extendedProps?.serviceUser?.name, "Unknown")}
                height={60}
                width={60}
                src={extendedProps?.serviceUser?.avatar}
              />
              <Box sx={{ flex: "1 1 auto", minWidth: 0, margin: "0px 8px" }}>
                <h6
                  style={{
                    margin: "3px 0px",
                    color: " #000",
                    fontSize: " 20px",
                    fontWeight: "500",
                    width: "100%",
                    whiteSpace: " nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {displayText(extendedProps?.serviceUser?.name, "Unknown")}
                </h6>
                <span
                  style={{
                    margin: "3px",
                    color: "var(--1-f-4970, #1F4970)",
                    fontSize: " 14px",
                    fontWeight: "500",
                  }}
                >
                  {!dayjs(selectedVisit?.start)
                    .utc()
                    .isSame(dayjs(selectedVisit?.end).utc(), "day") ? (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span>
                        Start:{" "}
                        {dayjs(selectedVisit?.start)
                          .utc()
                          .format("dddd DD MMM YYYY, hh:mm A")}
                      </span>
                      <span>
                        End:{" "}
                        {dayjs(selectedVisit?.end)
                          .utc()
                          .format("dddd DD MMM YYYY, hh:mm A")}
                      </span>
                    </div>
                  ) : (
                    <>
                      {dayjs(selectedVisit?.start).utc().format("HH:mm")} -{" "}
                      {dayjs(selectedVisit?.end).utc().format("HH:mm")},{" "}
                      {dayjs(selectedVisit?.start).utc().format("DD MMM YYYY")}
                    </>
                  )}
                </span>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                flex: "0 0 auto",
                gap: 1,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  background: extendedProps?.isCancelled
                    ? "rgba(0, 0, 0, 0.3)"
                    : !isUnAllocated
                      ? occurrenceColor.color + "30"
                      : "#9F9F9E30",
                  color: extendedProps?.isCancelled
                    ? "#000"
                    : !isUnAllocated
                      ? occurrenceColor.color
                      : "#9F9F9E",
                  fontSize: "12px",
                  fontWeight: "500",
                  height: "38px",
                  alignItems: "center",
                  width: { xs: "84px", sm: "100px" },
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "0.2rem",
                  textTransform: "capitalize",
                }}
              >
                {extendedProps?.isCancelled
                  ? "Cancelled"
                  : !isUnAllocated
                    ? occurrenceColor.status
                    : "Unassigned"}
              </Box>
              <IconButton
                aria-label="Close visit details"
                onClick={onClose}
                size="small"
                sx={{ flexShrink: 0, padding: "4px" }}
              >
                <SVG.Cross width="20px" />
              </IconButton>
            </Box>
          </Box>

          <Box sx={schedulerDrawerTabsSx}>
            {TABS.map((tab) => (
              <div
                key={tab.value}
                onClick={() => dispatch(setCurrentTab(tab.value))}
                className="Tabs"
                style={{
                  margin: "10px",
                  fontSize: "12px",
                  fontWeight: currentTab === tab.value ? "600" : "500",
                  color:
                    currentTab === tab.value
                      ? "#651F70"
                      : accessibleInteractiveColors.navigationBlue,
                  borderRadius: currentTab === tab.value ? "2px " : "inherit",
                  borderBottom:
                    currentTab === tab.value ? "2px solid #651F70" : "inherit",
                  cursor: "pointer",
                }}
              >
                {tab.label}
              </div>
            ))}
          </Box>
        </Box>
        <Box role="presentation">
          <Box sx={schedulerDrawerContentSx}>{renderTab(currentTab)}</Box>
        </Box>
      </Drawer>
    </DrawerStyled>
  );
}

export default SchedulerDrawer;
